"use client";

import { useState } from "react";
import {
  ErrorNotice,
  Notice,
  PillGroup,
  SelectField,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { BUTTON_SMALL, LABEL, WIDGET_H2 } from "@/components/tool-kit/styles";
import {
  LightPanel,
  type LightStrings,
  WavefrontFigure,
} from "@/components/tools/physics-waves/doppler-panels";
import {
  QuantityField,
  WorkingSteps,
} from "@/components/tools/physics-waves/quantity-field";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";
import {
  emittedFrequency,
  mach,
  observedFrequency,
  type SoundResult,
  sourceSpeedFromShift,
} from "@/lib/tool-kit/physics-waves/doppler";
import {
  formatSig,
  type NumberFormatter,
  withSiPrefix,
} from "@/lib/tool-kit/physics-waves/format";
import {
  findUnit,
  HERTZ_UNITS,
  SPEED_UNITS,
  toBase,
  type UnitDef,
} from "@/lib/tool-kit/physics-waves/units";

/** English strings. A translated content file overrides these through `ui`. */
export const DOPPLER_EFFECT_CALCULATOR_STRINGS = {
  modeLabel: "What kind of wave?",
  modes: {
    sound: "Sound (moving source or listener)",
    light: "Light and redshift",
  },
  unitFor: "Unit for {name}",
  sigFigs: "Round to this many significant figures",
  results: "Results",
  working: "The working",
  enterAll: "Enter a number in every box to see the result.",
  tryLabel: "Try an example",
  presets: {
    approaching: "Ambulance approaching",
    receding: "Ambulance moving away",
    walking: "Walking toward a speaker",
  },
  goalLabel: "What do you want to find?",
  goals: {
    observed: "The frequency heard",
    emitted: "The frequency emitted",
    speed: "The source's speed",
  },
  speedOfSound: "Speed of sound",
  speedOfSoundHint: "343 m/s in air at 20 °C; 331 m/s at 0 °C.",
  sourceFrequency: "Frequency emitted by the source",
  heardFrequency: "Frequency heard by the observer",
  observerSpeed: "Observer's speed",
  sourceSpeed: "Source's speed",
  observerDirection: "Observer is moving",
  sourceDirection: "Source is moving",
  observerToward: "Toward the source",
  observerAway: "Away from the source",
  sourceToward: "Toward the observer",
  sourceAway: "Away from the observer",
  convention:
    "Convention: the observer's speed counts as positive toward the source, and the source's speed as positive toward the observer. Air is taken to be at rest.",
  resultHeard: "Frequency heard",
  resultEmitted: "Frequency emitted",
  resultSource: "Source's speed",
  resultShift: "Change in frequency",
  resultReversed: "If both directions were reversed",
  resultMach: "Source speed as a Mach number",
  towardWord: "toward the observer",
  awayWord: "away from the observer",
  atRestWord: "at rest",
  stepObserved:
    "f′ = f × (v {observerSign} {vo}) ÷ (v {sourceSign} {vs}) = {f} × ({v} {observerSign} {vo}) ÷ ({v} {sourceSign} {vs}) = {result}",
  stepEmitted:
    "f = f′ × (v {sourceSign} {vs}) ÷ (v {observerSign} {vo}) = {f} × ({v} {sourceSign} {vs}) ÷ ({v} {observerSign} {vo}) = {result}",
  stepSpeed:
    "vₛ = v − f × (v + vₒ) ÷ f′ = {v} − {f} × ({v} + {vo}) ÷ {fobs} = {result}",
  errSupersonic:
    "The source is moving at or faster than the speed of sound, so the formula no longer applies: the waves pile up into a shock wave (a sonic boom) instead of a shifted pitch.",
  errObserver:
    "The observer is moving away at least as fast as the sound itself, so the sound never reaches them.",
  errPositive: "Frequencies and the speed of sound must be above zero.",
  figureLabel: "Wavefronts from a moving source",
  figureDescription:
    "Circular wavefronts spreading from a moving source, bunched together ahead of it and spread out behind it.",
  lightGoalLabel: "What do you know?",
  lightGoals: {
    speed: "The speed",
    z: "The redshift z",
    wavelengths: "Two wavelengths",
  },
  lightSpeed: "Speed of the source",
  lightDirection: "The source is",
  lightReceding: "Moving away (redshift)",
  lightApproaching: "Coming closer (blueshift)",
  lightZ: "Redshift (z)",
  lightRest: "Rest wavelength (optional)",
  lightRestHint:
    "The wavelength the source emits; adds the wavelength you would observe.",
  lightObservedInput: "Observed wavelength",
  lightEmittedInput: "Emitted (rest) wavelength",
  lightRedshift: "Redshift as a percentage",
  lightBlueshift: "Blueshift as a percentage",
  lightBeta: "Speed as a fraction of c (β)",
  lightSpeedKm: "Speed along the line of sight",
  lightClassical: "Classical estimate, z ≈ v/c",
  lightClassicalError: "Classical estimate's error",
  lightObservedWavelength: "Observed wavelength",
  lightFrequencyRatio: "Observed ÷ emitted frequency",
  lightStepWavelengths:
    "z = λ_obs ÷ λ_emit − 1 = {observed} ÷ {rest} − 1 = {z}",
  lightStepZ: "1 + z = √((1 + β) ÷ (1 − β)), with β = {beta}, so z = {z}",
  lightStepBeta:
    "β = ((1 + z)² − 1) ÷ ((1 + z)² + 1) = {beta}, so the speed is {v}",
  lightStepObserved:
    "λ_obs = λ_rest × (1 + z) = {rest} × (1 + {z}) = {observed}",
  lightTooFast: "A source cannot move at or faster than the speed of light.",
  lightInvalidZ:
    "That redshift is impossible: z must be greater than −1, and wavelengths above zero.",
} as const;

type Strings = typeof DOPPLER_EFFECT_CALCULATOR_STRINGS;
type Mode = keyof Strings["modes"];
type Goal = keyof Strings["goals"];
type PresetKey = keyof Strings["presets"];

const SIG_CHOICES = ["3", "4", "5"] as const;
const SOUND_SPEED_UNITS: UnitDef[] = SPEED_UNITS.slice(0, 4);

interface Entry {
  value: string;
  unit: string;
}

interface SoundState {
  goal: Goal;
  v: Entry;
  f: Entry;
  fobs: Entry;
  vo: Entry;
  vs: Entry;
  observerToward: boolean;
  sourceToward: boolean;
}

const INITIAL: SoundState = {
  goal: "observed",
  v: { value: "343", unit: "m/s" },
  f: { value: "700", unit: "Hz" },
  fobs: { value: "767", unit: "Hz" },
  vo: { value: "0", unit: "m/s" },
  vs: { value: "30", unit: "m/s" },
  observerToward: true,
  sourceToward: true,
};

const PRESETS: Record<PresetKey, Partial<SoundState>> = {
  approaching: {
    goal: "observed",
    f: { value: "700", unit: "Hz" },
    vo: { value: "0", unit: "m/s" },
    vs: { value: "30", unit: "m/s" },
    sourceToward: true,
    observerToward: true,
  },
  receding: {
    goal: "observed",
    f: { value: "700", unit: "Hz" },
    vo: { value: "0", unit: "m/s" },
    vs: { value: "30", unit: "m/s" },
    sourceToward: false,
    observerToward: true,
  },
  walking: {
    goal: "observed",
    f: { value: "440", unit: "Hz" },
    vo: { value: "1.5", unit: "m/s" },
    vs: { value: "0", unit: "m/s" },
    sourceToward: true,
    observerToward: true,
  },
};

function read(entry: Entry, units: readonly UnitDef[]): number | null {
  const parsed = parseNumberInput(entry.value);
  return parsed === null ? null : toBase(parsed, findUnit(units, entry.unit));
}

function signedPart(value: number): [string, string] {
  return value >= 0
    ? ["+", String(Math.abs(value))]
    : ["−", String(Math.abs(value))];
}

function soundMessage(
  result: Extract<SoundResult, { ok: false }>,
  strings: Strings
): string {
  return result.error === "supersonic"
    ? strings.errSupersonic
    : strings.errObserver;
}

interface Numbers {
  v: number;
  f: number | null;
  fobs: number | null;
  vo: number;
  vs: number;
}

function numbersFrom(state: SoundState): Numbers | null {
  const v = read(state.v, SOUND_SPEED_UNITS);
  const vo = read(state.vo, SOUND_SPEED_UNITS);
  const vs = read(state.vs, SOUND_SPEED_UNITS);
  if (v === null || vo === null) {
    return null;
  }
  if (state.goal !== "speed" && vs === null) {
    return null;
  }
  return {
    v,
    f: read(state.f, HERTZ_UNITS),
    fobs: read(state.fobs, HERTZ_UNITS),
    vo: state.observerToward ? vo : -vo,
    vs: state.sourceToward ? (vs ?? 0) : -(vs ?? 0),
  };
}

interface ResultProps {
  state: SoundState;
  n: Numbers;
  fmt: NumberFormatter;
  sig: number;
  strings: Strings;
}

function SpeedResults({ n, fmt, sig, strings }: ResultProps) {
  const show = (value: number, unit: string) =>
    withSiPrefix(fmt, value, unit, sig);
  const plain = (value: number) => formatSig(fmt, value, sig);
  const speed = sourceSpeedFromShift(n.f ?? 0, n.fobs ?? 0, n.v, n.vo);
  if (speed === null) {
    return <ErrorNotice>{strings.errObserver}</ErrorNotice>;
  }
  let direction: string = strings.atRestWord;
  if (speed > 0) {
    direction = strings.towardWord;
  } else if (speed < 0) {
    direction = strings.awayWord;
  }
  return (
    <>
      <StatGrid>
        <StatCard
          emphasis
          label={strings.resultSource}
          value={show(Math.abs(speed), "m/s")}
        />
        <StatCard label={strings.sourceDirection} value={direction} />
        <StatCard
          label={strings.resultMach}
          value={plain(mach(Math.abs(speed), n.v))}
        />
      </StatGrid>
      <WorkingSteps
        heading={strings.working}
        lines={[
          t(strings.stepSpeed, {
            v: plain(n.v),
            vo: plain(n.vo),
            f: plain(n.f ?? 0),
            fobs: plain(n.fobs ?? 0),
            result: `${show(speed, "m/s")} (${direction})`,
          }),
        ]}
      />
    </>
  );
}

function FrequencyResults({ state, n, fmt, sig, strings }: ResultProps) {
  const show = (value: number, unit: string) =>
    withSiPrefix(fmt, value, unit, sig);
  const plain = (value: number) => formatSig(fmt, value, sig);
  const observed = state.goal === "observed";
  const given = (observed ? n.f : n.fobs) ?? 1;
  const solve = (vo: number, vs: number) =>
    observed
      ? observedFrequency(given, n.v, vo, vs)
      : emittedFrequency(given, n.v, vo, vs);
  const result = solve(n.vo, n.vs);
  if (!result.ok) {
    return <ErrorNotice>{soundMessage(result, strings)}</ErrorNotice>;
  }
  const reversed = solve(-n.vo, -n.vs);
  const change = ((result.frequency - given) / given) * 100;
  const [oSign, oAbs] = signedPart(n.vo);
  const sourceSign = n.vs >= 0 ? "−" : "+";
  return (
    <>
      <StatGrid>
        <StatCard
          emphasis
          label={observed ? strings.resultHeard : strings.resultEmitted}
          value={show(result.frequency, "Hz")}
        />
        <StatCard
          label={strings.resultShift}
          value={`${change > 0 ? "+" : ""}${plain(change)}%`}
        />
        {reversed.ok ? (
          <StatCard
            label={strings.resultReversed}
            value={show(reversed.frequency, "Hz")}
          />
        ) : null}
        <StatCard
          label={strings.resultMach}
          value={plain(mach(Math.abs(n.vs), n.v))}
        />
      </StatGrid>
      <WavefrontFigure
        approaching={n.vs >= 0}
        machNumber={Math.abs(n.vs) / n.v}
        strings={strings}
      />
      <WorkingSteps
        heading={strings.working}
        lines={[
          t(observed ? strings.stepObserved : strings.stepEmitted, {
            v: plain(n.v),
            vo: oAbs,
            vs: String(Math.abs(n.vs)),
            observerSign: oSign,
            sourceSign,
            f: plain(given),
            result: show(result.frequency, "Hz"),
          }),
        ]}
      />
    </>
  );
}

function SoundResults(props: ResultProps) {
  const { state, n, strings } = props;
  const needsSource = state.goal !== "emitted";
  const needsHeard = state.goal !== "observed";
  if (
    n.v <= 0 ||
    (needsSource && (n.f ?? 0) <= 0) ||
    (needsHeard && (n.fobs ?? 0) <= 0)
  ) {
    return <ErrorNotice>{strings.errPositive}</ErrorNotice>;
  }
  return state.goal === "speed" ? (
    <SpeedResults {...props} />
  ) : (
    <FrequencyResults {...props} />
  );
}

function SoundPanel({
  strings,
  fmt,
  sig,
}: {
  strings: Strings;
  fmt: NumberFormatter;
  sig: number;
}) {
  const [state, setState] = useState(INITIAL);
  const set = (patch: Partial<SoundState>) =>
    setState((current) => ({ ...current, ...patch }));
  const numbers = numbersFrom(state);
  const field = (
    key: "v" | "f" | "fobs" | "vo" | "vs",
    label: string,
    units: readonly UnitDef[],
    hint?: string
  ) => (
    <QuantityField
      hint={hint}
      id={`de-${key}`}
      label={label}
      onChange={(value) => set({ [key]: { ...state[key], value } })}
      onUnitChange={(unit) => set({ [key]: { ...state[key], unit } })}
      unit={state[key].unit}
      unitLabel={t(strings.unitFor, { name: label })}
      units={units}
      value={state[key].value}
    />
  );
  return (
    <>
      <PillGroup
        label={strings.goalLabel}
        name="de-goal"
        onChange={(goal) => set({ goal })}
        options={(Object.keys(strings.goals) as Goal[]).map((value) => ({
          value,
          label: strings.goals[value],
        }))}
        value={state.goal}
      />
      <div className="flex flex-wrap items-center gap-2">
        <span className={LABEL}>{strings.tryLabel}</span>
        {(Object.keys(PRESETS) as PresetKey[]).map((key) => (
          <button
            className={BUTTON_SMALL}
            key={key}
            onClick={() => set(PRESETS[key])}
            type="button"
          >
            {strings.presets[key]}
          </button>
        ))}
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        {field(
          "v",
          strings.speedOfSound,
          SOUND_SPEED_UNITS,
          strings.speedOfSoundHint
        )}
        {state.goal === "emitted"
          ? field("fobs", strings.heardFrequency, HERTZ_UNITS)
          : field("f", strings.sourceFrequency, HERTZ_UNITS)}
        {state.goal === "speed"
          ? field("fobs", strings.heardFrequency, HERTZ_UNITS)
          : null}
        {field("vo", strings.observerSpeed, SOUND_SPEED_UNITS)}
        {state.goal === "speed"
          ? null
          : field("vs", strings.sourceSpeed, SOUND_SPEED_UNITS)}
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <PillGroup
          label={strings.observerDirection}
          name="de-observer-direction"
          onChange={(value) => set({ observerToward: value === "toward" })}
          options={[
            { value: "toward", label: strings.observerToward },
            { value: "away", label: strings.observerAway },
          ]}
          value={state.observerToward ? "toward" : "away"}
        />
        {state.goal === "speed" ? null : (
          <PillGroup
            label={strings.sourceDirection}
            name="de-source-direction"
            onChange={(value) => set({ sourceToward: value === "toward" })}
            options={[
              { value: "toward", label: strings.sourceToward },
              { value: "away", label: strings.sourceAway },
            ]}
            value={state.sourceToward ? "toward" : "away"}
          />
        )}
      </div>
      <p className="text-muted-foreground text-xs">{strings.convention}</p>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {numbers === null ? (
          <Notice>{strings.enterAll}</Notice>
        ) : (
          <SoundResults
            fmt={fmt}
            n={numbers}
            sig={sig}
            state={state}
            strings={strings}
          />
        )}
      </section>
    </>
  );
}

export default function DopplerEffectCalculatorWidget() {
  const strings = useToolUi(DOPPLER_EFFECT_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const [mode, setMode] = useState<Mode>("sound");
  const [sigText, setSigText] = useState<(typeof SIG_CHOICES)[number]>("4");
  const sig = Number(sigText);
  const lightStrings: LightStrings = strings;
  return (
    <>
      <PillGroup
        label={strings.modeLabel}
        name="de-mode"
        onChange={setMode}
        options={(Object.keys(strings.modes) as Mode[]).map((value) => ({
          value,
          label: strings.modes[value],
        }))}
        value={mode}
      />
      <div className="max-w-xs">
        <SelectField
          id="de-sig"
          label={strings.sigFigs}
          onChange={setSigText}
          options={SIG_CHOICES.map((value) => ({ value, label: value }))}
          value={sigText}
        />
      </div>
      {mode === "sound" ? (
        <SoundPanel fmt={fmt} sig={sig} strings={strings} />
      ) : (
        <LightPanel fmt={fmt} sig={sig} strings={lightStrings} />
      )}
    </>
  );
}
